import type { ReactNode } from "react";
import type { Pt } from "../types";
import type { SolverLaneLite } from "../derive";
import type { SolverRoom, SolverRampInt, RampWall } from "../client";
import type { PickAttrs } from "../inspect";
import { t } from "../../i18n";

// Fallback ONLY. Dimensiunile locului vin din document (`geometry.w/h`, scrise
// de `architecture/canonical.py` din constantele modulului care le așază);
// astea rămân pentru documentele emise înainte ca ele să existe. Erau singura
// sursă, iar `5.0` nu se potrivea cu `STALL_LEN = 5.5` din solver: fiecare loc
// desenat era cu o jumătate de metru mai scurt decât cel rezervat.
const STALL_W = 2.5, STALL_D = 5.0;   // fallback; a bay carries its own w/d
export type Space = { cx: number; cy: number; deg: number; kind?: string; w?: number; d?: number };

const pts = (a: Pt[]) => a.map((p) => `${p[0]},${p[1]}`).join(" ");

export function Deck({ boundary }: { boundary: Pt[] }) {
  return <polygon points={pts(boundary)} fill="var(--paper)" stroke="var(--ink)"
    strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />;
}

/** Props every inspectable glyph carries.
 *
 *  `pick` is the `data-pick` attribute pair from `inspect.pickAttrs` — NOT a
 *  click handler. Pointer events stay on the SVG root, where panning and
 *  measuring live, and the Editor reads `data-pick` off whatever was pressed;
 *  see the long note on `pickAttrs` for why a handler here broke both.
 *
 *  An empty `pick` means "not interactive", which is how the PDF export path
 *  keeps its SVG free of inert attributes and cursor styles. */
export interface Pickable { pick?: PickAttrs; selected?: boolean }

/** Painted OVER the objects and never a pick target itself: a stall number, a
 *  dimension line, a direction arrow, an evacuation route. Without this the
 *  topmost painted element at the pointer is the label rather than the stall,
 *  `closest("[data-pick]")` finds nothing, and clicking the number printed on a
 *  bay dismisses the panel instead of describing the bay. */
export const DECOR = { pointerEvents: "none" as const };

/** The selection outline: one colour, one weight, everywhere. Drawn OVER the
 *  glyph rather than replacing its fill, so a selected stall still reads as a
 *  stall and a selected room keeps its hatch. */
export const SEL_STROKE = {
  fill: "none", stroke: "var(--sel-line)", strokeWidth: 3,
  vectorEffect: "non-scaling-stroke" as const, pointerEvents: "none" as const,
};

// The shared access strip between a pair of accessible bays: reserved floor, not a
// parking space and not drivable, so it is drawn hatched rather than filled.
export function AdaAisle({ a, pick, selected }: {
  a: { cx: number; cy: number; deg: number; w: number; h: number };
} & Pickable) {
  return (
    <g transform={`translate(${a.cx} ${a.cy}) rotate(${a.deg})`} {...pick}>
      <rect x={-a.h / 2} y={-a.w / 2} width={a.h} height={a.w}
        fill="url(#pg-ada-hatch)" stroke="var(--car-ada-line)" strokeWidth={1}
        vectorEffect="non-scaling-stroke" />
      {selected && <rect x={-a.h / 2} y={-a.w / 2} width={a.h} height={a.w} {...SEL_STROKE} />}
    </g>
  );
}

export function Bay({ s, pick, selected }: { s: Space } & Pickable) {
  const ada = s.kind === "ada";
  // Gabaritul e al locului, din document: 5,00 perpendicular, 5,75 paralel.
  const W = s.w ?? STALL_W, D = s.d ?? STALL_D;
  return (
    <g transform={`translate(${s.cx} ${s.cy}) rotate(${s.deg})`}
       data-pick={pick?.["data-pick"]}
       // `color` is the car glyph's currentColor and has to survive, so the
       // cursor is merged in here rather than spreading `pick` wholesale.
       style={{ color: ada ? "var(--car-ada-line)" : "var(--car-line)",
                ...(pick?.style ?? {}) }}>
      <rect x={-W / 2} y={-D / 2} width={W} height={D}
        fill="var(--stall)" stroke="var(--stall-line)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <use href="#pg-car" x={-W / 2 + 0.18} y={-D / 2 + 0.3}
        width={W - 0.36} height={D - 0.6} />
      {ada && (
        // Colour alone would not survive a monochrome print, and the norm asks for
        // a distinct pictogram on the space.
        <g transform={`translate(0 ${D / 2 - 0.95})`} fill="var(--car-ada-line)">
          <circle cx={0} cy={-0.42} r={0.17} />
          <path d="M -0.30 0.62 L -0.06 -0.10 L 0.30 -0.10"
            fill="none" stroke="var(--car-ada-line)" strokeWidth={0.13}
            strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={-0.02} cy={0.40} r={0.34} fill="none"
            stroke="var(--car-ada-line)" strokeWidth={0.13} />
        </g>
      )}
      {selected && (
        <rect x={-W / 2} y={-D / 2} width={W} height={D}
          {...SEL_STROKE} />
      )}
    </g>
  );
}

/** Peretele de zidărie de pe latura unei rampe -1→-2: o fâșie de grosimea lui
 *  în jurul axei, plină, ca un perete tăiat în plan. */
export function RampWallView({ w }: { w: RampWall }) {
  const L = Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1;
  const nx = (-(w.y2 - w.y1) / L) * (w.t / 2), ny = ((w.x2 - w.x1) / L) * (w.t / 2);
  const pts = [[w.x1 + nx, w.y1 + ny], [w.x2 + nx, w.y2 + ny], [w.x2 - nx, w.y2 - ny], [w.x1 - nx, w.y1 - ny]]
    .map((q) => q.join(",")).join(" ");
  return <polygon points={pts} fill="var(--ink)" stroke="var(--ink)" strokeWidth={0.5}
    vectorEffect="non-scaling-stroke" pointerEvents="none" />;
}

const FIRE_KINDS = new Set(["scara", "bazin"]);   // REI 180 outline per reference
const HATCH_KINDS = new Set(["rezerva", "pompe", "boxe"]);
export function RoomView({ r, pick, selected }: { r: SolverRoom } & Pickable) {
  const fire = FIRE_KINDS.has(r.kind);
  return (
    <g transform={`translate(${r.cx} ${r.cy}) rotate(${r.deg})`} {...pick}>
      <rect x={-r.w / 2} y={-r.h / 2} width={r.w} height={r.h}
        fill="var(--core)" stroke={fire ? "var(--fire)" : "var(--ink)"}
        strokeWidth={fire ? 2.4 : 1.2} vectorEffect="non-scaling-stroke" />
      {selected && <rect x={-r.w / 2} y={-r.h / 2} width={r.w} height={r.h} {...SEL_STROKE} />}
      {HATCH_KINDS.has(r.kind) && (
        <rect x={-r.w / 2} y={-r.h / 2} width={r.w} height={r.h} fill="url(#pg-hatch)" />
      )}
      <g transform={`rotate(${-r.deg})`}>
        {(() => {
          // Fit the label inside the room: shrink the font for narrow rooms
          // (monospace glyphs are ~0.6em wide), never below legibility.
          const fit = Math.max(0.42, Math.min(0.8, (Math.max(r.w, r.h) * 0.92) / (r.label.length * 0.62)));
          return <text x={0} y={-0.35} fontSize={fit} textAnchor="middle" fill="var(--ink)">{r.label}</text>;
        })()}
        <text x={0} y={0.75} fontSize={0.6} textAnchor="middle" fill="#525252">{r.area.toFixed(2)} m²</text>
      </g>
    </g>
  );
}

// --------------------------------------------------------------------------- //
// the stair core
// --------------------------------------------------------------------------- //
//
// A03 does not draw a core as rooms — it draws it as SYMBOLS inside one fire
// enclosure, and that is the whole difference between the reference sheet and a
// row of labelled rectangles:
//
//   * the enclosure is a 30 cm REI 180 wall drawn as a hatched BAND, not a line;
//   * the two shafts carry a diagonal cross and a door leaf on the lobby face;
//   * the stair is a real dog-leg — landing, two flights, a well between them,
//     goings at their true 28 cm pitch, the break line across, and UP at the
//     foot — drawn at its own size rather than stretched to fill the room;
//   * every SAS door is a leaf plus a swing arc, tagged EI 60-c, because the
//     airlock only means anything as two rated doors in series.
//
// Drawn in the enclosure's own frame, with the shafts located from the actual
// lift rooms rather than from a copy of the block's arithmetic — so it stays
// right if `rooms.build_core_block` ever rearranges them.

const WALL = 0.30;        // A03: 30 cm REI 180 enclosure wall
// The flight, dimensioned off A03: the sheet gives 430 x 270 overall, 120 per
// flight with a 30 well between them, and numbers the treads 1..11 up the near
// run and 12..19 back along the far one.
const STAIR_RUN = 4.30;
const STAIR_ACR = 2.70;
const FLIGHT_W = 1.20;
const WELL = 0.30;
const N_NEAR = 11, N_FAR = 8;
const DOOR_W = 0.95;      // A03 tags every core door 95/210

type R = { cx: number; cy: number; w: number; h: number; deg: number };

/** A point in `base`'s local frame (x right, y toward the SAS). */
function local(p: { cx: number; cy: number }, base: R) {
  const a = (base.deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  const dx = p.cx - base.cx, dy = p.cy - base.cy;
  return { x: dx * c + dy * s, y: -dx * s + dy * c };
}

/** Door as a plan symbol: jambs, leaf swung 90°, dashed swing arc. The opening
 *  lies on y=0 spanning ±wd/2 and the leaf swings to +y. The rating tag is NOT
 *  drawn here: the caller flips this symbol to face the escape direction, and a
 *  tag placed in this frame flips with it — which put one of them a metre
 *  outside the block, on the parking. The caller sites its own. */
function Door({ wd = DOOR_W }: { wd?: number }) {
  const h = wd / 2;
  return (
    <g>
      <line x1={-h} y1={-0.16} x2={-h} y2={0.16} stroke="var(--fire)"
        strokeWidth={1.6} vectorEffect="non-scaling-stroke" />
      <line x1={h} y1={-0.16} x2={h} y2={0.16} stroke="var(--fire)"
        strokeWidth={1.6} vectorEffect="non-scaling-stroke" />
      <line x1={-h} y1={0} x2={-h} y2={wd} stroke="var(--fire)"
        strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
      <path d={`M ${-h} ${wd} A ${wd} ${wd} 0 0 1 ${h} 0`} fill="none"
        stroke="var(--fire)" strokeWidth={0.7} vectorEffect="non-scaling-stroke" />
    </g>
  );
}

/** The dog-leg flight: landing, two runs, the well between them, goings at their
 *  true pitch, the break line, and UP at the foot.
 *
 *  Drawn to REAL proportions and centred in whatever floor the enclosure leaves
 *  it, never stretched to fill it — a flight scaled to a 35 m² room is the
 *  barcode the previous glyph drew. The run lies along the region's longer side,
 *  so the same stair reads correctly whether it stands beside the shafts or
 *  beyond them. `rot` is the enclosure's own rotation, so UP stays upright. */
function Stair({ x0, y0, x1, y1, rot }: {
  x0: number; y0: number; x1: number; y1: number; rot: number;
}) {
  const rw = x1 - x0, rh = y1 - y0;
  const horiz = rw >= rh;
  const availRun = (horiz ? rw : rh) - 0.2, availAcr = (horiz ? rh : rw) - 0.2;
  const k = Math.min(1, availRun / STAIR_RUN, availAcr / STAIR_ACR);
  if (k < 0.45) return null;                // below this the goings merge to grey
  const sw = STAIR_RUN * k, sh = STAIR_ACR * k;
  const fw = FLIGHT_W * k, well = WELL * k;
  const near = sw / N_NEAR;                 // the near run is numbered 1..11
  const far = sw / N_NEAR;                  // ... the far one 12..19, 8 of them
  const farRun = far * N_FAR;
  const offRun = ((horiz ? rw : rh) - sw) / 2, offAcr = ((horiz ? rh : rw) - sh) / 2;
  const place = horiz
    ? `translate(${x0 + offRun} ${y0 + offAcr})`
    : `translate(${x0 + offAcr + sh} ${y0 + offRun}) rotate(90)`;
  const num = k > 0.75 && near > 0.3;       // numbers only when they can be read
  const g: ReactNode[] = [];
  // near run: 11 goings across the full length, numbered from the foot
  for (let i = 1; i < N_NEAR; i++) {
    const x = near * i;
    g.push(<line key={`n${i}`} x1={x} y1={fw + well} x2={x} y2={sh}
      stroke="var(--ink)" strokeWidth={0.55} vectorEffect="non-scaling-stroke" />);
  }
  // far run: 8 goings, the shortfall against the near run IS the half-landing
  for (let i = 1; i < N_FAR; i++) {
    const x = sw - far * i;
    g.push(<line key={`f${i}`} x1={x} y1={0} x2={x} y2={fw}
      stroke="var(--ink)" strokeWidth={0.55} vectorEffect="non-scaling-stroke" />);
  }
  if (num) {
    for (let i = 0; i < N_NEAR; i++)
      g.push(<text key={`tn${i}`} x={near * (i + 0.5)} y={sh - fw / 2 + 0.09 * k}
        fontSize={0.26 * k} textAnchor="middle" fill="var(--ink)"
        transform={`rotate(${-rot - (horiz ? 0 : 90)} ${near * (i + 0.5)} ${sh - fw / 2})`}>{i + 1}</text>);
    for (let i = 0; i < N_FAR; i++)
      g.push(<text key={`tf${i}`} x={sw - far * (i + 0.5)} y={fw / 2 + 0.09 * k}
        fontSize={0.26 * k} textAnchor="middle" fill="var(--ink)"
        transform={`rotate(${-rot - (horiz ? 0 : 90)} ${sw - far * (i + 0.5)} ${fw / 2})`}>{i + 12}</text>);
  }
  const bx = near * 5.5, bo = sh * 0.5;     // break line, across the near run
  const mid = sh - fw / 2;                   // the ascending run's centreline
  return (
    <g transform={place}>
      <rect x={0} y={0} width={sw} height={sh} fill="none" stroke="var(--ink)"
        strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
      {g}
      {/* the well: two separate runs with a 30 cm slot between them, closed at
          the head by the half-landing the flight turns on */}
      <rect x={0} y={fw} width={farRun} height={well} fill="none"
        stroke="var(--ink)" strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
      <line x1={farRun} y1={0} x2={farRun} y2={fw + well} stroke="var(--ink)"
        strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
      {/* break line: one slanted cut across the run being climbed */}
      <line x1={bx - bo / 2} y1={sh + 0.05} x2={bx + bo / 2} y2={fw + well - 0.05}
        stroke="var(--ink)" strokeWidth={0.7} vectorEffect="non-scaling-stroke" />
      {/* UP, at the foot and OUTSIDE the flight — A03 puts it clear of the run.
          A travel arrow drawn along the run instead lands on the tread numbers
          and reads as a twentieth going. */}
      <g transform={`translate(${-0.5} ${mid}) rotate(${-rot - (horiz ? 0 : 90)})`}>
        <text x={0} y={-0.1} fontSize={0.4} textAnchor="middle" fill="var(--ink)">UP</text>
        <line x1={-0.3} y1={0.18} x2={0.24} y2={0.18} stroke="var(--ink)"
          strokeWidth={0.7} vectorEffect="non-scaling-stroke" />
        <polygon fill="var(--ink)" points={`${0.34},${0.18} ${0.06},${0.05} ${0.06},${0.31}`} />
      </g>
    </g>
  );
}

/** A core is ONE drawing assembled from up to four room records — the REI 180
 *  enclosure, its lift shafts and the airlock beyond the end wall. It is picked
 *  as one object and reports the ENCLOSURE, because that is the room the plan
 *  names and the one the rules (P118-99's REI 180, the evacuation limit) attach
 *  to; the shafts inside it have no separate rule to show. */
export function CoreView({ scara, lifts, sas, pick, selected }: {
  scara: SolverRoom; lifts: SolverRoom[]; sas: SolverRoom | null;
} & Pickable) {
  const { w, h } = scara;
  const x0 = -w / 2 + WALL, x1 = w / 2 - WALL;
  const y0 = -h / 2 + WALL, y1 = h / 2 - WALL;

  // shafts, in the enclosure's frame — the bank sits on the lift face (-y),
  // the stair compartment fills the +y end, and the corridor strip between
  // them is the lobby the SAS continues past the end wall. This is the
  // apartment core's own cross-section; the enclosure just fires around it.
  const shafts = lifts.map((l) => {
    const c = local(l, scara);
    return { x: c.x - l.w / 2, y: c.y - l.h / 2, w: l.w, h: l.h };
  });
  // With no shaft records — a streamed frame, or a core the matcher could not
  // assemble — the flight takes the whole enclosure rather than nothing: an
  // empty hatched box says less than a stair drawn in the wrong half.
  const bankBot = shafts.length ? Math.max(...shafts.map((s) => s.y + s.h)) : y0;
  const bankLeft = shafts.length ? Math.min(...shafts.map((s) => s.x)) : x0;
  const strip = shafts.length ? (sas ? sas.h : 2.1) : 0;
  const stripBot = Math.min(y1, bankBot + strip);

  return (
    <g transform={`translate(${scara.cx} ${scara.cy}) rotate(${scara.deg})`}
       {...pick}>
      {/* enclosure floor */}
      <rect x={-w / 2} y={-h / 2} width={w} height={h} fill="var(--core)" />
      {selected && <rect x={-w / 2} y={-h / 2} width={w} height={h} {...SEL_STROKE} />}
      {/* REI 180 wall, as a hatched band between the outer and inner faces */}
      <path fillRule="evenodd" fill="url(#pg-fire-hatch)" stroke="var(--fire)"
        strokeWidth={1.8} vectorEffect="non-scaling-stroke"
        d={`M ${-w / 2} ${-h / 2} h ${w} v ${h} h ${-w} Z `
          + `M ${x0} ${y0} h ${x1 - x0} v ${y1 - y0} h ${x0 - x1} Z`} />

      {/* the flight fills the stair compartment beyond the strip — the same
          floor the apartment stair stands on, which is the point */}
      <Stair x0={x0} y0={stripBot} x1={x1} y1={y1} rot={scara.deg} />

      {/* A03 divides the bank with a 20 cm wall; the rooms are emitted flush,
          so open the gap when drawing rather than letting two shafts share one
          line and read as a single box. */}
      {shafts.map((s) => {
        const l = shafts.some((o) => Math.abs(o.x + o.w - s.x) < 0.05);
        const r = shafts.some((o) => Math.abs(s.x + s.w - o.x) < 0.05);
        return { ...s, x: s.x + (l ? 0.1 : 0), w: s.w - (l ? 0.1 : 0) - (r ? 0.1 : 0) };
      }).map((s, i) => (
        <g key={`lf${i}`}>
          {/* A03 draws the shaft as the structure AND the car inside it: an
              outer box, an inset cage carrying the diagonals, and the landing
              door as a rectangle across the lobby face rather than a line. */}
          <rect x={s.x} y={s.y} width={s.w} height={s.h} fill="#ffffff"
            stroke="var(--ink)" strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
          <rect x={s.x + 0.12} y={s.y + 0.12} width={s.w - 0.24} height={s.h - 0.34}
            fill="none" stroke="var(--ink)" strokeWidth={0.7} vectorEffect="non-scaling-stroke" />
          <line x1={s.x + 0.12} y1={s.y + 0.12} x2={s.x + s.w - 0.12} y2={s.y + s.h - 0.22}
            stroke="var(--ink)" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
          <line x1={s.x + s.w - 0.12} y1={s.y + 0.12} x2={s.x + 0.12} y2={s.y + s.h - 0.22}
            stroke="var(--ink)" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
          <rect x={s.x + 0.16} y={s.y + s.h - 0.2} width={s.w - 0.32} height={0.13}
            fill="none" stroke="var(--ink)" strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
        </g>
      ))}

      {/* The SAS: the corridor strip carried PAST the enclosure's end wall as a
          short vestibule — A03's 5.2 m² box, not a band across the block. Two
          EI 60-c doors in series: parking -> SAS at its far end, SAS -> lobby
          through the shared wall; both swing the way you escape. */}
      {sas && (() => {
        const c = local(sas, scara);
        const sx0 = c.x - sas.w / 2, sx1 = c.x + sas.w / 2;
        const sy0 = c.y - sas.h / 2, sy1 = c.y + sas.h / 2;
        const side = c.x >= 0 ? 1 : -1;            // which end wall it continues
        const inner = side > 0 ? sx0 : sx1;        // shared wall with enclosure
        const outer = side > 0 ? sx1 : sx0;        // parking end
        const dmid = (sy0 + sy1) / 2;
        return (
          <g>
            <rect x={sx0} y={sy0} width={sas.w} height={sas.h} fill="var(--core)" />
            {/* rated walls along the vestibule's two long sides */}
            <path fillRule="evenodd" fill="url(#pg-fire-hatch)" stroke="var(--fire)"
              strokeWidth={1.8} vectorEffect="non-scaling-stroke"
              d={`M ${sx0} ${sy0} h ${sas.w} v ${sas.h} h ${-sas.w} Z `
                + `M ${sx0} ${sy0 + WALL} h ${sas.w} v ${sas.h - 2 * WALL} h ${-sas.w} Z`} />
            {/* Both leaves sweep the way you escape — parking into the airlock,
                airlock into the lobby — so BOTH swing toward the enclosure and
                neither arc reaches the parking. The outer one used to swing the
                other way and its arc was drawn across the stalls. */}
            <g transform={`translate(${inner - side * WALL / 2} ${dmid}) rotate(${side > 0 ? 90 : 270})`}>
              <Door />
            </g>
            <g transform={`translate(${outer} ${dmid}) rotate(${side > 0 ? 90 : 270})`}>
              <Door />
            </g>
            <g transform={`translate(${(sx0 + sx1) / 2} ${dmid}) rotate(${-scara.deg})`}>
              <text x={0} y={-0.28} fontSize={0.4} textAnchor="middle" fill="var(--ink)">SAS</text>
              <text x={0} y={0.36} fontSize={0.3} textAnchor="middle" fill="var(--fire)">EI 60-c</text>
            </g>
          </g>
        );
      })()}

      {/* label on the lobby strip, between the shaft doors and the flight —
          the floor A03 writes it on */}
      <g transform={`translate(${(x0 + bankLeft) / 2 - 0.1} ${(y0 + bankBot) / 2}) rotate(${scara.deg === 90 || scara.deg === 270 ? -scara.deg : 0})`}>
        <text x={0} y={-0.15} fontSize={0.4} textAnchor="middle"
          transform="rotate(90)" fill="var(--ink)" />
      </g>
      {/* The label is counter-rotated to stay upright, so what it has to fit in
          is the strip's WORLD-horizontal extent — the strip's length on an
          upright core, but only its 2.3 m depth on one turned 90°, where
          "CASA SCARII" at full size ran straight over the shafts. */}
      {(() => {
        const upright = ((Math.round(scara.deg) % 180) + 180) % 180 === 0;
        const along = upright ? x1 - x0 : stripBot - bankBot;
        const fit = Math.max(0.26, Math.min(0.44, (along * 0.94) / (11 * 0.6)));
        return (
          <g transform={`translate(${(x0 + x1) / 2 - (sas ? DOOR_W / 2 : 0)} ${(bankBot + stripBot) / 2}) rotate(${-scara.deg})`}>
            <text x={0} y={-0.1} fontSize={fit} textAnchor="middle" fill="var(--ink)">CASA SCARII</text>
            <text x={0} y={fit + 0.24} fontSize={fit * 0.8} textAnchor="middle" fill="#525252">{scara.area.toFixed(2)} m²</text>
          </g>
        );
      })()}
    </g>
  );
}

/** Rooms, with each stair core drawn as ONE object. The shafts and the airlock
 *  are matched to their enclosure geometrically (a shaft's centre falls inside
 *  it, an airlock abuts one of its END walls at strip level) rather than by
 *  their order in the list, so a core still assembles correctly however the
 *  solver emitted it. */
export function RoomsLayer({ rooms, pickOf, selected }: {
  rooms: SolverRoom[];
  /** Given a room's index in `rooms` — the ORIGINAL index, not a position in
   *  the assembled core list — the `data-pick` attributes for it. A core
   *  reports its enclosure's index, since that is the room the plan names and
   *  the one P118-99's REI 180 attaches to. */
  pickOf?: (i: number) => PickAttrs;
  selected?: number | null;
}) {
  const taken = new Set<number>();
  const cores: { i: number; scara: SolverRoom; lifts: SolverRoom[];
                 sas: SolverRoom | null }[] = [];
  rooms.forEach((sc, i) => {
    if (sc.kind !== "scara") return;
    taken.add(i);
    const lifts: SolverRoom[] = [];
    // tracked by INDEX, not by reference: two identical cores produce equal
    // room records, and indexOf would then claim the first one's airlock twice
    let sasAt = -1, sasD = Infinity;
    rooms.forEach((r, j) => {
      // holul de evacuare nu e SAS-ul nucleului, chiar dacă stă lipit de el
      if (taken.has(j) || j === i || r.evacuare) return;
      const c = local(r, sc);
      if (r.kind === "lift" && Math.abs(c.x) <= sc.w / 2 && Math.abs(c.y) <= sc.h / 2) {
        lifts.push(r); taken.add(j);
      } else if (r.kind === "sas") {
        // abutting an END wall: just past ±w/2, within the enclosure's depth
        const d = Math.abs(Math.abs(c.x) - (sc.w + r.w) / 2);
        if (d < 0.6 && Math.abs(c.y) <= sc.h / 2 + 0.6 && d < sasD) { sasAt = j; sasD = d; }
      }
    });
    if (sasAt >= 0) taken.add(sasAt);
    cores.push({ i, scara: sc, lifts, sas: sasAt >= 0 ? rooms[sasAt] : null });
  });
  return (
    <g>
      {cores.map((c, k) => (
        <CoreView key={`co${k}`} scara={c.scara} lifts={c.lifts} sas={c.sas}
          pick={pickOf?.(c.i)} selected={selected === c.i} />
      ))}
      {/* Camerele ALA (adăpost, SAS, grup sanitar, ieșiri) și holurile SAS ale
          căilor de evacuare nu se desenează aici: `StoreyView` le desenează pe
          cele din document, pe conturul lor, cu pereții, obiectele sanitare și
          numele lor — aici ar fi a doua oară, peste ele. */}
      {rooms.map((r, i) => taken.has(i) || r.kind.startsWith("ala") || r.evacuare ? null : (
        <RoomView key={`r${i}`} r={r}
          pick={pickOf?.(i)} selected={selected === i} />
      ))}
    </g>
  );
}

export function RoadSurface({ deck, lanes }: { deck?: [number, number][][]; lanes: SolverLaneLite[] }) {
  // Preferred: one clean, continuous polygon from the solver (aisle union,
  // morphologically closed + simplified) filled as a single shape — no ragged
  // edges. `deck` is a flat list of rings (outer + holes); even-odd cuts holes.
  if (deck && deck.length) {
    const d = deck
      .map((ring) => "M" + ring.map((p) => `${p[0]} ${p[1]}`).join(" L") + "Z")
      .join(" ");
    return <path d={d} fill="var(--aisle)" fillRule="evenodd" />;
  }
  // Fallback (streaming incumbents, before the deck polygon exists): fat lanes.
  return (
    <g>
      {lanes.map((l, i) => (
        // butt caps: a square cap would extend the surface half a lane (2.5 m)
        // past each end and spill over the lot boundary.
        <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
          stroke="var(--aisle)" strokeWidth={5} strokeLinecap="butt" />
      ))}
    </g>
  );
}

/** Rampa de ACCES (parter → −1), desenată ca rampă de garaj:
 *
 *    * GURA — primii 6 m, la 5 %, cât ține racordarea cu strada: umplutură
 *      deschisă, muchie punctată la capăt și scris pe ea «5 %», ca să se vadă
 *      că acolo panta e mică;
 *    * PANTA — restul, cu TRIUNGHIURI care arată sensul de coborâre (simbolul
 *      obișnuit de rampă) și bordurile îngroșate pe laturile lungi;
 *    * PODEAUA DE MANEVRĂ din fața gurii de jos, punctată;
 *    * textul ORIZONTAL, ca numerele locurilor: «rampă acces», panta și sensul.
 *      Rotit odată cu rampa ieșea cu capul în jos (Oana, 19.09.2026).
 *
 *  Nu se inventează nicio geometrie: ce nu e în document nu se desenează. */
export function AccessRampView({ r }: {
  r: { poly: [number, number][]; mouth?: [number, number][]; axis?: [number, number][];
       slope?: number; runM?: number; twoWay?: boolean; direction?: "down" | "up" | "both";
       apron?: [number, number][];
       helix?: boolean; geometry?: string; axisPath?: [number, number][] };
}) {
  const pts = r.poly.map((q) => `${q[0]},${q[1]}`).join(" ");
  const n = r.poly.length;
  const cx = r.poly.reduce((a, q) => a + q[0], 0) / n;
  const cy = r.poly.reduce((a, q) => a + q[1], 0) / n;
  const ax = r.axis && r.axis.length === 2 ? r.axis : null;
  // COTITĂ la 90° (tangentă + arc + segment drept): corpul e un poligon în L, iar
  // galoanele și săgeata merg pe `axisPath`, nu pe o axă dreaptă
  const traseu = r.geometry === "cotita" && (r.axisPath?.length ?? 0) >= 2 ? r.axisPath! : null;
  const drept = !!ax && !r.helix && !traseu;
  let deg = 0, lung = 0, w = 5.5;
  if (ax) {
    const [a, b] = ax;
    deg = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
    lung = Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  if (r.mouth && r.mouth.length >= 4) {
    // lățimea rampei = latura scurtă a gurii
    const [p0, p1, p2] = r.mouth;
    w = Math.min(Math.hypot(p1[0] - p0[0], p1[1] - p0[1]),
                 Math.hypot(p2[0] - p1[0], p2[1] - p1[1]));
  }
  const GURA = 6;                      // cei 6 m la 5 % de la marginea carosabilului
  const hw = w / 2;
  // TRIUNGHIURILE de rampă: un rând de vârfuri spre sensul de coborâre
  const triunghiuri: ReactNode[] = [];
  if (drept && lung > GURA + 2) {
    const de_la = GURA + 0.6, pana = lung - 1.4;
    const pas = 1.5;
    for (let x = de_la; x + pas <= pana; x += pas) {
      triunghiuri.push(
        <polygon key={`t${x}`} points={`${x},${-hw + 0.25} ${x + pas * 0.8},0 ${x},${hw - 0.25}`}
          fill="none" stroke="var(--ink)" strokeWidth={0.6} strokeOpacity={0.5}
          vectorEffect="non-scaling-stroke" />);
    }
  }
  const peTraseu: ReactNode[] = [];
  if (traseu) {
    const L = lungimeTraseu(traseu);
    // galoanele de la capătul celor 6 m la 5 % până înaintea gurii de jos
    for (let s = GURA + 0.6; s + 1.2 <= L - 1.4; s += 1.5) {
      const { p, tg, nn } = laStatia(traseu, s);
      const v: [number, number] = [p[0] + tg[0] * 1.2, p[1] + tg[1] * 1.2];
      peTraseu.push(
        <polyline key={`g${s}`} fill="none" stroke="var(--ink)" strokeWidth={0.6} strokeOpacity={0.5}
          vectorEffect="non-scaling-stroke"
          points={`${p[0] + nn[0] * (hw - 0.25)},${p[1] + nn[1] * (hw - 0.25)} ${v[0]},${v[1]} `
                + `${p[0] - nn[0] * (hw - 0.25)},${p[1] - nn[1] * (hw - 0.25)}`} />);
    }
    // muchia dintre cei 6 m la 5 % și pantă, de-a curmezișul tangentei
    const m6 = laStatia(traseu, GURA);
    peTraseu.push(<line key="m6" x1={m6.p[0] - m6.nn[0] * hw} y1={m6.p[1] - m6.nn[1] * hw}
      x2={m6.p[0] + m6.nn[0] * hw} y2={m6.p[1] + m6.nn[1] * hw} stroke="var(--ink)" strokeWidth={0.8}
      strokeDasharray="0.8 0.6" vectorEffect="non-scaling-stroke" />);
    // săgeata sensului de coborâre, la gura de jos
    const f = laStatia(traseu, L - 0.5);
    peTraseu.push(<polygon key="sg" fill="var(--ink)"
      points={`${f.p[0]},${f.p[1]} ${f.p[0] - tg2(f, 1.9, 0.75)[0]},${f.p[1] - tg2(f, 1.9, 0.75)[1]} `
            + `${f.p[0] - tg2(f, 1.9, -0.75)[0]},${f.p[1] - tg2(f, 1.9, -0.75)[1]}`} />);
  }
  const panta = r.slope ? `${Math.round(r.slope * 100)} %` : "";
  // sensul de mers vine din document (`direction`, 3.23): pe rampa cu un fir
  // spune și încotro — intrare (coboară) sau ieșire (urcă)
  const sens = r.direction === "down" ? "sens unic, intrare"
    : r.direction === "up" ? "sens unic, ieșire"
    : r.twoWay == null ? "" : r.twoWay ? "dublu sens" : "sens unic";
  // punctele pentru textele orizontale: mijlocul gurii și mijlocul pantei
  const pe = (t: number): [number, number] =>
    ax ? [ax[0][0] + (ax[1][0] - ax[0][0]) * t, ax[0][1] + (ax[1][1] - ax[0][1]) * t] : [cx, cy];
  const mijlocGura = traseu ? laStatia(traseu, GURA / 2).p : ax ? pe(Math.min(0.5, GURA / (2 * lung))) : [cx, cy];
  return (
    <g>
      <polygon points={pts} fill="#b9bab7" fillOpacity={0.55} stroke="var(--ink)"
        strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <polygon points={pts} fill="url(#pg-hatch)" pointerEvents="none" />
      {r.apron && r.apron.length >= 3 && (
        <polygon points={r.apron.map((q) => `${q[0]},${q[1]}`).join(" ")}
          fill="var(--aisle)" fillOpacity={0.3} stroke="var(--ink)" strokeWidth={0.8}
          strokeDasharray="1.2 0.8" vectorEffect="non-scaling-stroke" pointerEvents="none" />
      )}
      {/* gura: la cotă, pantă mică — umplutură deschisă, fără hașură */}
      {r.mouth && r.mouth.length >= 2 && (
        <polygon points={r.mouth.map((q) => `${q[0]},${q[1]}`).join(" ")}
          fill="var(--aisle)" stroke="var(--ink)" strokeWidth={1.6}
          vectorEffect="non-scaling-stroke" />
      )}
      {drept && (
        <g transform={`translate(${ax![0][0]} ${ax![0][1]}) rotate(${deg})`}>
          {/* bordurile, pe laturile lungi */}
          <line x1={0} y1={-hw} x2={lung} y2={-hw} stroke="var(--ink)" strokeWidth={1.6}
            vectorEffect="non-scaling-stroke" />
          <line x1={0} y1={hw} x2={lung} y2={hw} stroke="var(--ink)" strokeWidth={1.6}
            vectorEffect="non-scaling-stroke" />
          {/* muchia dintre cei 6 m la 5 % și panta propriu-zisă */}
          <line x1={GURA} y1={-hw} x2={GURA} y2={hw} stroke="var(--ink)" strokeWidth={0.8}
            strokeDasharray="0.8 0.6" vectorEffect="non-scaling-stroke" />
          {triunghiuri}
          {/* săgeata sensului de coborâre, la capătul de jos */}
          <polygon points={`${lung - 2.4},${-0.75} ${lung - 0.5},0 ${lung - 2.4},0.75`}
            fill="var(--ink)" />
        </g>
      )}
      {peTraseu}
      {/* TEXTELE, orizontale — ca numerele locurilor */}
      <text x={cx} y={cy - 0.5} fontSize={0.75} textAnchor="middle" dominantBaseline="central"
        fill="var(--ink)" style={DECOR}>{t("a01.rampa_acces")}</text>
      {panta && (
        <text x={cx} y={cy + 0.6} fontSize={0.7} textAnchor="middle" dominantBaseline="central"
          fill="var(--ink)" style={DECOR}>{[panta, sens].filter(Boolean).join(" · ")}</text>
      )}
      {(drept || traseu) && (
        <text x={mijlocGura[0]} y={mijlocGura[1]} fontSize={0.6} textAnchor="middle"
          dominantBaseline="central" fill="var(--ink)" style={DECOR}>5 %</text>
      )}
      <title>
        {[t("a01.rampa_acces"), traseu ? "cotită la 90°" : "", sens, panta ? `pantă ${panta}` : "",
          r.runM ? `${r.runM} m desfășurat` : "",
          "primii 6 m la 5 %"].filter(Boolean).join(" · ")}
      </title>
    </g>
  );
}

// The interior (-1 -> -2) ramp: a hatched slope band with a single travel arrow
// and a "rampă → nivel …" label sitting ON the band. Drawn in a local frame
// (translate to centre, rotate to the run heading): the run lies along local +x
// (the DOWN-slope direction, from the -1 mouth toward the -2 landing).
//   up=false (level -1): arrow points DOWN the slope — from -1 you descend to -2.
//   up=true  (level -2): arrow points UP the slope — from -2 you go up to -1
//                        (the only way out of the garage is via -1).
// Fully self-contained (no shared pattern/marker defs).
/** Rampa elicoidală (E, H, M3): corpul e un SECTOR DE INEL, nu o bandă dreaptă,
 *  iar `cx`/`cy` sunt centrul curbei. Se desenează conturul primit din document,
 *  cu galoanele pe arc și săgeata la capătul dinspre care se merge. */
function RampIntHelix({ r, label, up, pick, selected }:
  { r: SolverRampInt; label: string; up: boolean } & Pickable) {
  const poly = (r.poly ?? []).map((q) => q.join(",")).join(" ");
  const [cx, cy] = r.center ?? [r.cx, r.cy];
  const ri = r.rInt ?? 0, re = r.rExt ?? r.w, rm = (ri + re) / 2, hw = (re - ri) / 2;
  const a0 = Math.atan2(r.top[1] - cy, r.top[0] - cx);
  const sweep = ((r.sweepDeg ?? 180) * Math.PI) / 180;
  // pe -1 mașina coboară (de la `top` spre `bottom`), pe -2 urcă înapoi
  const sens = up ? -1 : 1;
  const la = (t: number) => {
    const a = a0 + sweep * (up ? 1 - t : t);
    const p: [number, number] = [cx + rm * Math.cos(a), cy + rm * Math.sin(a)];
    const dir = sweep >= 0 ? 1 : -1;
    const tg: [number, number] = [-Math.sin(a) * dir * sens, Math.cos(a) * dir * sens];
    const nn: [number, number] = [Math.cos(a), Math.sin(a)];
    return { p, tg, nn };
  };
  const n = Math.max(3, Math.round(Math.abs(sweep) / 0.35));
  const chev: ReactNode[] = [];
  for (let i = 1; i < n; i++) {
    const { p, tg, nn } = la(i / n);
    const v = p[0] + tg[0] * 0.5, w = p[1] + tg[1] * 0.5;   // vârful galonului
    for (const sgn of [1, -1]) {
      const x = p[0] - tg[0] * 0.4 + nn[0] * sgn * hw * 0.8;
      const y = p[1] - tg[1] * 0.4 + nn[1] * sgn * hw * 0.8;
      chev.push(<line key={`${i}${sgn}`} x1={x} y1={y} x2={v} y2={w} stroke="var(--ink)"
        strokeWidth={0.6} vectorEffect="non-scaling-stroke" opacity={0.45} />);
    }
  }
  const fin = la(1), cap = fin.p, tg = fin.tg, nn = fin.nn;
  const vf: [number, number] = [cap[0] + tg[0] * 0.9, cap[1] + tg[1] * 0.9];
  const sageata = [vf, [cap[0] - tg[0] * 0.3 + nn[0] * 0.7, cap[1] - tg[1] * 0.3 + nn[1] * 0.7],
                   [cap[0] - tg[0] * 0.3 - nn[0] * 0.7, cap[1] - tg[1] * 0.3 - nn[1] * 0.7]];
  const mij = la(0.5).p;
  const keep = ((up ? r.keep2 : r.keep1) ?? []).filter((k) => k.length >= 3);
  return (
    <g {...pick}>
      {keep.map((k, i) => (
        <polygon key={`k${i}`} points={k.map((q) => q.join(",")).join(" ")}
          fill="var(--aisle)" fillOpacity={0.35} stroke="var(--ink)" strokeWidth={0.8}
          strokeDasharray="1.2 0.8" vectorEffect="non-scaling-stroke" />
      ))}
      <polygon points={poly} fill={r.dedesubt ? "none" : "var(--aisle)"}
        stroke="var(--ink)" strokeWidth={1.2}
        strokeDasharray={r.dedesubt ? "2 1.4" : undefined}
        vectorEffect="non-scaling-stroke" />
      {selected && <polygon points={poly} {...SEL_STROKE} />}
      {chev}
      <polygon points={sageata.map((q) => q.join(",")).join(" ")} fill="var(--ink)" />
      {/* textul stă orizontal, ca tot ce scrie pe planșă */}
      <text x={mij[0]} y={mij[1]} fontSize={1.3} textAnchor="middle" dominantBaseline="central"
        fill="var(--ink)">{label}</text>
      <title>{`${label} · elicoidală ${Math.abs(r.sweepDeg ?? 180)}°, rază ${rm.toFixed(1)} m`}</title>
    </g>
  );
}

/** Lungimea unei polilinii. */
function lungimeTraseu(path: [number, number][]) {
  let L = 0;
  for (let i = 1; i < path.length; i++) L += Math.hypot(path[i][0] - path[i - 1][0], path[i][1] - path[i - 1][1]);
  return L;
}

/** Punctul, tangenta și normala unei polilinii la lungimea `s` de la început
 *  (tăiată la capete). Pe ea se pun galoanele și săgeata unei rampe cotite. */
function laStatia(path: [number, number][], s: number) {
  let rest = Math.max(0, s);
  for (let i = 1; i < path.length; i++) {
    const dx = path[i][0] - path[i - 1][0], dy = path[i][1] - path[i - 1][1];
    const l = Math.hypot(dx, dy);
    if (l < 1e-9) continue;
    if (rest <= l || i === path.length - 1) {
      const k = Math.min(1, rest / l);
      const tg: [number, number] = [dx / l, dy / l];
      const p: [number, number] = [path[i - 1][0] + dx * k, path[i - 1][1] + dy * k];
      return { p, tg, nn: [-tg[1], tg[0]] as [number, number] };
    }
    rest -= l;
  }
  const p: [number, number] = [path[0][0], path[0][1]];
  return { p, tg: [1, 0] as [number, number], nn: [0, 1] as [number, number] };
}

/** Vectorul `a·tangentă + b·normală` al unei stații. */
function tg2(st: { tg: [number, number]; nn: [number, number] }, a: number, b: number): [number, number] {
  return [st.tg[0] * a + st.nn[0] * b, st.tg[1] * a + st.nn[1] * b];
}

/** Rampa -1 → -2 COTITĂ la 90° (K separată, SK sub accesul cotit): corpul e un
 *  poligon în L (segment drept de 5,50 + sfert de inel de 6,50), deci nu se
 *  poate desena din `cx`/`cy`/`run`/`deg`. Se desenează conturul primit, cu
 *  galoanele pe `axisPath` și săgeata la capătul dinspre care se merge. */
function RampIntCotita({ r, label, up, pick, selected }:
  { r: SolverRampInt; label: string; up: boolean } & Pickable) {
  const poly = (r.poly ?? []).map((q) => q.join(",")).join(" ");
  const path = r.axisPath!;
  const L = lungimeTraseu(path);
  const hw = r.w / 2;
  // pe -1 se coboară de la `top` spre `bottom` (înainte pe traseu), pe -2 se urcă înapoi
  const chev: ReactNode[] = [];
  for (let s = 1.5; s + 1.2 <= L - 1.2; s += 1.5) {
    const st = laStatia(path, s);
    const dir = up ? -1 : 1;
    const v: [number, number] = [st.p[0] + st.tg[0] * 0.5 * dir, st.p[1] + st.tg[1] * 0.5 * dir];
    for (const sgn of [1, -1]) {
      const x = st.p[0] - st.tg[0] * 0.4 * dir + st.nn[0] * sgn * hw * 0.8;
      const y = st.p[1] - st.tg[1] * 0.4 * dir + st.nn[1] * sgn * hw * 0.8;
      chev.push(<line key={`${s}${sgn}`} x1={x} y1={y} x2={v[0]} y2={v[1]} stroke="var(--ink)"
        strokeWidth={0.6} vectorEffect="non-scaling-stroke" opacity={0.45} />);
    }
  }
  const cap = laStatia(path, up ? 0.5 : L - 0.5);
  const dir = up ? -1 : 1;
  const vf = [cap.p[0] + cap.tg[0] * 0.9 * dir, cap.p[1] + cap.tg[1] * 0.9 * dir];
  const sageata = [vf,
                   [cap.p[0] - cap.tg[0] * 0.3 * dir + cap.nn[0] * 0.7, cap.p[1] - cap.tg[1] * 0.3 * dir + cap.nn[1] * 0.7],
                   [cap.p[0] - cap.tg[0] * 0.3 * dir - cap.nn[0] * 0.7, cap.p[1] - cap.tg[1] * 0.3 * dir - cap.nn[1] * 0.7]];
  const mij = laStatia(path, L / 2).p;
  const keep = ((up ? r.keep2 : r.keep1) ?? []).filter((k) => k.length >= 3);
  return (
    <g {...pick}>
      {keep.map((k, i) => (
        <polygon key={`k${i}`} points={k.map((q) => q.join(",")).join(" ")}
          fill="var(--aisle)" fillOpacity={0.35} stroke="var(--ink)" strokeWidth={0.8}
          strokeDasharray="1.2 0.8" vectorEffect="non-scaling-stroke" />
      ))}
      {/* DEDESUBT = sub rampa de acces (SK): contur întrerupt, fără umplutură */}
      <polygon points={poly} fill={r.dedesubt ? "none" : "var(--aisle)"}
        stroke="var(--ink)" strokeWidth={1.2}
        strokeDasharray={r.dedesubt ? "2 1.4" : undefined}
        vectorEffect="non-scaling-stroke" />
      {selected && <polygon points={poly} {...SEL_STROKE} />}
      {chev}
      <polygon points={sageata.map((q) => q.join(",")).join(" ")} fill="var(--ink)" />
      <text x={mij[0]} y={mij[1]} fontSize={1.3} textAnchor="middle" dominantBaseline="central"
        fill="var(--ink)">{label}</text>
      <title>{`${label} · cotită la 90°, ${r.run} m pe ax`}</title>
    </g>
  );
}

export function RampIntView({ r, label, up = false, pick, selected }:
  { r: SolverRampInt; label: string; up?: boolean } & Pickable) {
  if (r.helix && (r.poly ?? []).length >= 3) {
    return <RampIntHelix r={r} label={label} up={up} pick={pick} selected={selected} />;
  }
  if (r.geometry === "cotita" && (r.poly ?? []).length >= 3 && (r.axisPath?.length ?? 0) >= 2) {
    return <RampIntCotita r={r} label={label} up={up} pick={pick} selected={selected} />;
  }
  const hr = r.run / 2, hw = r.w / 2;
  const chev: ReactNode[] = [];
  const n = Math.max(2, Math.floor(r.run / 3));
  for (let i = 1; i < n; i++) {
    const x = -hr + (r.run * i) / n;      // ">" chevrons opening down-slope (+x)
    chev.push(<line key={`${i}a`} x1={x - 0.8} y1={-hw} x2={x} y2={0}
      stroke="var(--ink)" strokeWidth={0.6} vectorEffect="non-scaling-stroke" opacity={0.45} />);
    chev.push(<line key={`${i}b`} x1={x - 0.8} y1={hw} x2={x} y2={0}
      stroke="var(--ink)" strokeWidth={0.6} vectorEffect="non-scaling-stroke" opacity={0.45} />);
  }
  const ah = 0.9;                          // arrowhead half-height / reach
  const flip = r.deg > 90 && r.deg < 270;  // keep the label text upright
  // arrow runs to one end; head at that end (down = +x, up = -x)
  const tail = up ? hr - 1 : -hr + 1, tip = up ? -hr + 0.5 : hr - 0.5;
  const base = up ? -hr + 1.5 : hr - 1.5;
  // podeaua de manevră a nivelului: la -1 palierul din care se intră pe rampă,
  // la -2 cei 6 m din fața gurii de jos (Oana, 22.09.2026). Contur întrerupt,
  // în coordonatele planului — nu se rotește cu rampa
  const keep = ((up ? r.keep2 : r.keep1) ?? []).filter((k) => k.length >= 3);
  return (
    <g {...pick}>
    {keep.map((k, i) => (
      <polygon key={`k${i}`} points={k.map((p) => p.join(",")).join(" ")}
        fill="var(--aisle)" fillOpacity={0.35} stroke="var(--ink)" strokeWidth={0.8}
        strokeDasharray="1.2 0.8" vectorEffect="non-scaling-stroke" />
    ))}
    <g transform={`translate(${r.cx} ${r.cy}) rotate(${r.deg})`}>
      {/* DEDESUBT = sub rampa de acces: contur întrerupt și fără umplutură, ca
          orice e desenat sub cota planșei — altfel ar acoperi rampa de acces
          care trece pe deasupra (Oana, 20.09.2026). */}
      <rect x={-hr} y={-hw} width={r.run} height={r.w}
        fill={r.dedesubt ? "none" : "var(--aisle)"}
        stroke="var(--ink)" strokeWidth={1.2}
        strokeDasharray={r.dedesubt ? "2 1.4" : undefined}
        vectorEffect="non-scaling-stroke" />
      {selected && <rect x={-hr} y={-hw} width={r.run} height={r.w} {...SEL_STROKE} />}
      {chev}
      <line x1={tail} y1={2 * ah / 3} x2={base} y2={2 * ah / 3} stroke="var(--ink)"
        strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <polygon points={`${base},${2 * ah / 3 - ah / 2} ${tip},${2 * ah / 3} ${base},${2 * ah / 3 + ah / 2}`}
        fill="var(--ink)" />
      {/* label sits ON the ramp band, upper half, upright */}
      <text x={0} y={-ah / 2} fontSize={1.3} textAnchor="middle" dominantBaseline="central"
        fill="var(--ink)" transform={flip ? `rotate(180)` : undefined}>{label}</text>
    </g>
    </g>
  );
}

export function StallNumber({ s, n }: { s: Space; n: number }) {
  // PDF style: bold, horizontal (upright regardless of stall rotation), sitting
  // over the windscreen area of the car.
  const a = (s.deg * Math.PI) / 180;
  const x = s.cx - Math.sin(a) * 0.9, y = s.cy + Math.cos(a) * 0.9;
  return (
    <text x={x} y={y} fontSize={0.75} fontWeight="bold" textAnchor="middle"
      dominantBaseline="central" fill="var(--ink)" style={DECOR}>{n}</text>
  );
}
